import { useEffect, useState } from 'react'
import { getNotificationCount, markNotificationsRead } from '@/lib/api'

let cachedCount = 0
let inFlightCountRequest: Promise<number> | null = null
let hasFetchedForSession = false

function getCachedCount() {
  return cachedCount
}

async function fetchCountOnce(): Promise<number> {
  if (inFlightCountRequest) return inFlightCountRequest

  inFlightCountRequest = getNotificationCount()
    .then((data) => {
      const next = data.unread ?? data.count ?? 0
      cachedCount = next
      return next
    })
    .finally(() => {
      inFlightCountRequest = null
    })

  return inFlightCountRequest
}

export function useNotificationCount(enabled = true) {
  const [count, setCount] = useState(getCachedCount)

  useEffect(() => {
    if (!enabled) {
      cachedCount = 0
      hasFetchedForSession = false
      setCount(0)
      return
    }

    if (hasFetchedForSession) {
      setCount(cachedCount)
      return
    }

    let cancelled = false

    hasFetchedForSession = true
    void fetchCountOnce()
      .then((next) => {
        if (!cancelled) setCount(next)
      })
      .catch(() => {
        // noop
      })

    return () => {
      cancelled = true
    }
  }, [enabled])

  async function markRead() {
    if (!enabled || cachedCount <= 0) return

    const previous = cachedCount
    cachedCount = 0
    setCount(0)

    try {
      await markNotificationsRead()
    } catch {
      cachedCount = previous
      setCount(previous)
    }
  }

  return { count, markRead }
}
